import React, { useCallback, useState } from 'react'
import { UseStepFunc, defaultStepFormProps } from '../types.d'
import { Route } from 'react-router-dom'
import * as routing from '../../constants/routes'
import { useCompleteOnSubmit } from '../use-complete-on-submit'
import { useTranslation } from 'react-i18next'
import { FormValues } from './types.d'
import { useDispatch } from 'react-redux'
import { useShouldExecuteStep } from '../use-should-execute-step'
import { OnboardingState } from 'app/auth/components/hooks/use-onboarding-setup'
import { OnboardingStepKey } from '@cbgms/api'
import { useShouldRunInBackground } from '../use-should-run-in-background'
import { ChangeTyreForm } from './ChangeTyreForm'
import { updateTyreChangeSettings } from 'app/settings/components/TyreChange/actions'
import { parseTyreTreshold } from 'app/settings/components/TyreChange/EditContainer'
import { TyreChangeCTA } from './TyreChangeCTA'
import { useStepData } from '../use-step-data'

interface StepInputProps {
  basePath: string
}

interface StepProps {
  basePath: string
  onSubmit: (v: FormValues) => void
  onboardingInfo: OnboardingState
}

export const useTyreChangeStep: UseStepFunc<StepProps, StepInputProps> = ({ basePath, onboardingInfo }) => {
  const [tyreChange, setTyreChange] = useState<FormValues | null>(null)

  const dispatch = useDispatch()
  const stepName = OnboardingStepKey.TyreChange
  const stepData = useStepData(stepName, onboardingInfo)
  const shouldExecuteStep = useShouldExecuteStep(stepName, onboardingInfo)
  const shouldRunInBackground = useShouldRunInBackground(stepName, onboardingInfo)

  const onStepSubmit = (values: FormValues) => {
    setTyreChange(values)
  }

  const complete = useCompleteOnSubmit(onStepSubmit)
  const onPersist = useCallback(async () => {
    if (tyreChange) {
      const settings: FormValues = {
        ...tyreChange,
        SummerMinThreshold: parseTyreTreshold(tyreChange.SummerMinThreshold),
        WinterMinThreshold: parseTyreTreshold(tyreChange.WinterMinThreshold)
      }
      await dispatch(updateTyreChangeSettings(settings))
    }
  }, [tyreChange, dispatch])

  return {
    Component: TyreChangeStep,
    CTAComponent: TyreChangeCTA,
    StepperText,
    isCompleted: complete.isCompleted,
    showInStepper: false,
    showStepperHeader: false,
    hideSkip: true,
    step: stepName,
    stepData,
    shouldExecuteStep,
    shouldRunInBackground,
    stepProps: { duplicationCheck: stepData.DuplicationCheck || [], basePath, onSubmit: complete.onSubmit, onboardingInfo },
    persister: {
      onPersist,
      persistOrder: 3
    }
  }
}

const StepperText: React.FC<{}> = () => {
  const { t } = useTranslation()
  return <>{t('onboarding.title_tyre_change')}</>
}

export const TyreChangeStep: React.FC<StepProps & defaultStepFormProps> = ({ basePath, ...rest }) => {
  return <Route path={routing.pathTyreChangeStep(basePath)} render={() => <ChangeTyreForm {...rest} />} />
}
